/**
 * How a resolved day reads on the muster, and what the month totals to.
 *
 * `buildAttendanceMonth` decides what a day *is*. This decides what the muster
 * *shows* for it, and how the row and column totals are counted. The two are
 * separate on purpose: the register writes a status in words, the muster writes
 * a mark in a 31-column grid, and the grid's rules are its own.
 *
 * The rule that matters:
 *
 *   Presence is the default, so it is a dot rather than a letter. Twenty-three
 *   tinted blocks per row drown out the two days that need attention, so only
 *   exceptions are named and tinted. A late arrival is still a day the employee
 *   was there — it is a dot, and the minutes live in the register.
 *
 * And the rule that is a correctness question rather than a design one:
 *
 *   A MISSING PUNCH IS NEVER COUNTED AS PRESENT. A day with an arrival and no
 *   departure is unverified; transcribing it as a present day onto a sheet
 *   somebody signs turns an open question into an assertion. It counts towards
 *   the working days it was — so the ratio shows the shortfall — and it has to
 *   be regularised before it can become presence.
 *
 * Present therefore reads against the working-day total, "23 / 25", because a
 * bare 23 means nothing to someone checking a month with a holiday in it.
 *
 * Every day carries a `dayString`/`dayNum` on the UTC calendar day, which is
 * what these functions key on — no Date is constructed here, so no local
 * timezone can shift a day into the neighbouring one.
 */

/** Kinds a muster cell can be. */
export const KIND = {
  PRESENT: "present",
  ABSENT: "absent",
  MISSING_PUNCH: "missingPunch",
  LEAVE: "leave",
  HOLIDAY: "holiday",
  WEEKLY_OFF: "weeklyOff",
  UPCOMING: "upcoming",
  UNKNOWN: "unknown",
};

/**
 * The days the month can be held against: the ones the employee was rostered
 * for. Holidays and weekly offs are not working days, and an upcoming day has
 * not happened, so neither belongs in the denominator. Leave does — a day taken
 * off was a working day that was scheduled away, and dropping it from the
 * denominator would quietly flatter a month with a fortnight of leave in it.
 */
export const WORKING_KINDS = new Set([
  KIND.PRESENT,
  KIND.ABSENT,
  KIND.MISSING_PUNCH,
  KIND.LEAVE,
]);

/**
 * What a day shows in the grid.
 *
 * @param day one entry from buildAttendanceMonth().days
 * @returns {{kind: string, symbol: string, tone: string, corrected: boolean}}
 *          `tone` is a statusTone key, or "none" for a cell with no fill.
 */
export const classifyDay = (day) => {
  const corrected = Boolean(day?.corrected);
  switch (day?.status?.key) {
    case "PRESENT":
    case "LATE":
      // the default, so it is a dot; lateness is the register's business
      return { kind: KIND.PRESENT, symbol: "·", tone: "none", corrected };
    case "MISSING_PUNCH":
      return { kind: KIND.MISSING_PUNCH, symbol: "MP", tone: "violet", corrected };
    case "ABSENT":
      return { kind: KIND.ABSENT, symbol: "A", tone: "danger", corrected };
    case "ON_LEAVE":
      // the leave type's own code — CL, SL, LWP — so a run of casual leave is
      // distinguishable from a run of sick leave at a glance
      return { kind: KIND.LEAVE, symbol: day.code || "L", tone: "info", corrected: false };
    case "HOLIDAY":
      return { kind: KIND.HOLIDAY, symbol: "H", tone: "muted", corrected: false };
    case "WEEKLY_OFF":
      // a blank column is a weekly off: naming every rest day would put five
      // more letters a week into a grid whose whole point is that letters are rare
      return { kind: KIND.WEEKLY_OFF, symbol: "", tone: "none", corrected: false };
    case "UPCOMING":
      return { kind: KIND.UPCOMING, symbol: "", tone: "none", corrected: false };
    default:
      // UNKNOWN / NOT_TRACKED — no claim is made about the day, and it is not
      // counted either way
      return { kind: KIND.UNKNOWN, symbol: day?.code || "?", tone: "muted", corrected: false };
  }
};

/**
 * The three figures at the end of a row, plus the denominator they read against.
 *
 * @param days buildAttendanceMonth().days
 * @returns {{present, absent, leave, missingPunch, workingDays}}
 */
export const tallyRow = (days = []) => {
  const t = { present: 0, absent: 0, leave: 0, missingPunch: 0, workingDays: 0 };
  (days || []).forEach((day) => {
    const { kind } = classifyDay(day);
    if (!WORKING_KINDS.has(kind)) return;
    t.workingDays += 1;
    if (kind === KIND.PRESENT) t.present += 1;
    else if (kind === KIND.ABSENT) t.absent += 1;
    else if (kind === KIND.LEAVE) t.leave += 1;
    else if (kind === KIND.MISSING_PUNCH) t.missingPunch += 1;
  });
  return t;
};

/**
 * The footer row: how many people were present on each day of the month.
 *
 * `expected` is how many of the shown rows were rostered that day, so a day
 * nobody was due — a shared holiday, a Sunday, a date still to come — reads as
 * a dash rather than a zero. `short` is the honest signal the footer colours
 * on: the team was below full strength that day. That is a fact about the data,
 * not a failed read.
 *
 * @param rows      rows carrying `days` from buildAttendanceMonth
 * @param totalDays days in the month
 * @returns {Array<{present:number, expected:number, short:boolean}>}
 */
export const tallyColumns = (rows = [], totalDays = 0) => {
  const cols = Array.from({ length: totalDays }, () => ({ present: 0, expected: 0 }));
  (rows || []).forEach((r) => {
    (r?.days || []).forEach((day) => {
      const i = (day?.dayNum ?? 0) - 1;
      if (i < 0 || i >= totalDays) return;
      const { kind } = classifyDay(day);
      if (!WORKING_KINDS.has(kind)) return;
      cols[i].expected += 1;
      if (kind === KIND.PRESENT) cols[i].present += 1;
    });
  });
  return cols.map((c) => ({ ...c, short: c.expected > 0 && c.present < c.expected }));
};

/**
 * The same day, for paper.
 *
 * A tint does not survive a black-and-white photocopier, so the dot becomes a
 * P. Weekly offs become WO for the same reason: on screen the blank column is
 * legible because the columns are ruled and adjacent, but a blank box in a
 * printed table reads as an omission somebody forgot to fill in.
 */
export const printSymbol = (day) => {
  const { kind, symbol, corrected } = classifyDay(day);
  if (kind === KIND.UPCOMING) return "";
  if (kind === KIND.WEEKLY_OFF) return "WO";
  const mark = kind === KIND.PRESENT ? "P" : symbol;
  return corrected ? `${mark}*` : mark;
};
